<template>
  <div ref="rootRef" class="header-search">
    <form class="yt-search" @submit.prevent="submitSearch">
      <div class="yt-search__field">
        <i class="fa-solid fa-magnifying-glass" aria-hidden="true" />
        <label class="sr-only" for="yt-header-search">{{ $t('home.searchPlaceholder') }}</label>
        <input
          id="yt-header-search"
          v-model="keyword"
          type="search"
          class="yt-search__input"
          :placeholder="$t('home.searchPlaceholder')"
          autocomplete="off"
          @focus="openPanel"
          @click="openPanel"
        />
      </div>

      <span class="yt-search__divider" aria-hidden="true" />

      <Location
        ref="locationRef"
        v-model="selectedLocations"
        @opened="closePanel"
      />

      <button type="submit" class="yt-search__submit" :title="$t('home.searchCta')">
        <span class="yt-search__submit-label">{{ $t('home.searchCta') }}</span>
        <i class="fa-solid fa-magnifying-glass yt-search__submit-icon" aria-hidden="true" />
      </button>
    </form>

    <div v-show="panelOpen" class="yt-panel">
      <div class="yt-panel__top">
        <div class="yt-panel__col">
          <div class="yt-panel__head">
            <h3>{{ $t('home.recentSearches') }}</h3>
            <button
              v-if="recentSearches.length"
              type="button"
              class="yt-panel__clear"
              @click="clearRecent"
            >
              {{ $t('home.clearAll') }}
            </button>
          </div>

          <ul v-if="recentSearches.length" class="yt-panel__recent">
            <li v-for="(item, idx) in recentSearches" :key="`${item.q}-${idx}`">
              <button type="button" class="yt-panel__recent-main" @click="applyRecent(item.q)">
                <i class="fa-regular fa-clock" aria-hidden="true" />
                <span class="yt-panel__recent-q">{{ item.q }}</span>
                <span v-if="item.count != null" class="yt-panel__recent-count">
                  ({{ item.count }} {{ $t('home.resultCount') }})
                </span>
              </button>
              <div class="yt-panel__recent-actions">
                <button
                  type="button"
                  class="yt-panel__icon-btn"
                  :title="$t('home.fillSearch')"
                  @click="keyword = item.q"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square" />
                </button>
                <button
                  type="button"
                  class="yt-panel__icon-btn"
                  :title="$t('home.remove')"
                  @click="removeRecent(idx)"
                >
                  <i class="fa-solid fa-xmark" />
                </button>
              </div>
            </li>
          </ul>
          <p v-else class="yt-panel__empty">{{ $t('home.noRecent') }}</p>
        </div>

        <div class="yt-panel__col">
          <div class="yt-panel__head">
            <h3>{{ $t('home.youMayLike') }}</h3>
          </div>
          <ul class="yt-panel__suggest">
            <li v-for="p in suggested" :key="p.productId ?? p.id">
              <button type="button" class="yt-panel__suggest-item" @click="goProduct(p)">
                <span
                  class="yt-panel__thumb"
                  :style="{ backgroundImage: p.imageDetail ? `url(${p.imageDetail})` : undefined }"
                />
                <span class="yt-panel__name">{{ p.productName }}</span>
                <span class="yt-panel__price">{{ formatPrice(p) }}</span>
              </button>
            </li>
          </ul>
          <p v-if="!suggested.length && !loadingSuggest" class="yt-panel__empty">
            {{ $t('home.noSuggest') }}
          </p>
        </div>
      </div>

      <div class="yt-panel__keywords">
        <h3>{{ $t('home.popularKeywords') }}</h3>
        <div class="yt-panel__tags">
          <button
            v-for="tag in popularKeywords"
            :key="tag"
            type="button"
            class="yt-panel__tag"
            @click="applyKeyword(tag)"
          >
            {{ tag }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiViewPost } from '@/api/index'
import { PRODUCTS_LIST_ALL } from '@/const/ApiRedis'
import Utils from '@/utils/Utils'
import router from '@/router'
import Location from './Location.vue'
import { SEARCH, productDetailPath } from '@/const/path'

const RECENT_KEY = 'home-search-recent'
const MAX_RECENT = 8

type RecentItem = { q: string; count?: number }

const { t } = useI18n()

const keyword = ref('')
const selectedLocations = ref<string[]>([])
const panelOpen = ref(false)
const recentSearches = ref<RecentItem[]>([])
const suggested = ref<any[]>([])
const loadingSuggest = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const locationRef = ref<{ close: () => void } | null>(null)

const popularKeywords = computed(() => [
  t('home.kwFinance'),
  t('home.kwBusiness'),
  t('home.kwIt'),
  t('home.kwAccountant'),
  t('home.kwMarketing'),
])

const loadRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) {
      recentSearches.value = []
      return
    }
    recentSearches.value = parsed
      .map((x: any) =>
        typeof x === 'string'
          ? { q: x }
          : x?.q
            ? { q: String(x.q), count: x.count }
            : null,
      )
      .filter(Boolean) as RecentItem[]
  } catch {
    recentSearches.value = []
  }
}

const saveRecent = () => {
  localStorage.setItem(RECENT_KEY, JSON.stringify(recentSearches.value.slice(0, MAX_RECENT)))
}

const pushRecent = (q: string) => {
  const trimmed = q.trim()
  if (!trimmed) return
  recentSearches.value = [
    { q: trimmed },
    ...recentSearches.value.filter((r) => r.q.toLowerCase() !== trimmed.toLowerCase()),
  ].slice(0, MAX_RECENT)
  saveRecent()
}

const clearRecent = () => {
  recentSearches.value = []
  saveRecent()
}

const removeRecent = (idx: number) => {
  recentSearches.value.splice(idx, 1)
  saveRecent()
}

const formatPrice = (p: any) => {
  if (!p?.minPrice) return t('text.contact')
  return `${Utils.parseMoney(String(p.minPrice))} ${p.currency || 'VNĐ'}`
}

const loadSuggested = async () => {
  if (suggested.value.length) return
  loadingSuggest.value = true
  try {
    const res: any = await apiViewPost({
      apiCode: PRODUCTS_LIST_ALL,
      page: 1,
      size: 5,
      isDesc: false,
      header: false,
      isUser: true,
      wheres: [],
      orders: [{ field: 'productId', direction: 'DESC' }],
    })
    suggested.value = res?.data?.data?.body || []
  } catch {
    suggested.value = []
  } finally {
    loadingSuggest.value = false
  }
}

const closePanel = () => {
  panelOpen.value = false
}

const openPanel = () => {
  locationRef.value?.close()
  panelOpen.value = true
  loadSuggested()
}

const onDocClick = (e: MouseEvent) => {
  const el = rootRef.value
  if (!el || el.contains(e.target as Node)) return
  if (panelOpen.value) closePanel()
  locationRef.value?.close()
}

const goSearch = (q?: string) => {
  const term = (q ?? keyword.value).trim()
  if (term) {
    keyword.value = term
    pushRecent(term)
  }
  const query: Record<string, string> = {}
  if (term) query.q = term
  if (selectedLocations.value.length) {
    query.location = selectedLocations.value.join(',')
  }
  closePanel()
  locationRef.value?.close()
  router.push({ path: SEARCH, query })
}

const submitSearch = () => goSearch()
const applyRecent = (q: string) => goSearch(q)
const applyKeyword = (tag: string) => goSearch(tag)

const goProduct = (p: any) => {
  const id = p.productId ?? p.id
  closePanel()
  if (id != null) router.push({ path: productDetailPath(id) })
}

onMounted(() => {
  loadRecent()
  document.addEventListener('mousedown', onDocClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocClick)
})
</script>

<style scoped>
.header-search {
  --yt-ink: #0f0f0f;
  --yt-surface: #f2f2f2;
  --yt-blue: #1967d2;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.yt-search {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: 4px 4px 4px 14px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.yt-search__field {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  color: #717171;
}

.yt-search__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 36px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: var(--yt-ink);
}

.yt-search__input::placeholder {
  color: #888;
}

.yt-search__divider {
  width: 1px;
  height: 24px;
  margin: 0 8px;
  background: #e0e0e0;
  flex: 0 0 auto;
}

.yt-search__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  height: 36px;
  margin-left: 4px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: #ff6a00;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.yt-search__submit:hover {
  background: #e85f00;
}

.yt-search__submit-icon {
  display: none;
}

.yt-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 30;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
}

.yt-panel__top {
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 24px;
  margin-bottom: 16px;
}

.yt-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.yt-panel__head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--yt-ink);
}

.yt-panel__clear {
  border: none;
  background: none;
  color: var(--yt-blue);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.yt-panel__recent,
.yt-panel__suggest {
  list-style: none;
  margin: 0;
  padding: 0;
}

.yt-panel__recent li {
  display: flex;
  align-items: center;
  gap: 4px;
}

.yt-panel__recent-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  padding: 8px 4px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  color: var(--yt-ink);
  font-size: 14px;
}

.yt-panel__recent-main i {
  color: #888;
  flex: 0 0 auto;
}

.yt-panel__recent-q {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.yt-panel__recent-count {
  flex: 0 0 auto;
  color: #888;
  font-size: 12px;
}

.yt-panel__recent-actions {
  display: flex;
  gap: 2px;
}

.yt-panel__icon-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #888;
  cursor: pointer;
}

.yt-panel__icon-btn:hover {
  background: var(--yt-surface);
}

.yt-panel__suggest-item {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 4px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
}

.yt-panel__thumb {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f0f0f0 center / cover no-repeat;
}

.yt-panel__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: var(--yt-ink);
}

.yt-panel__price {
  font-size: 13px;
  font-weight: 600;
  color: #ff6a00;
  white-space: nowrap;
}

.yt-panel__keywords h3 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 700;
}

.yt-panel__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.yt-panel__tag {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 999px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
}

.yt-panel__tag:hover {
  border-color: #ff6a00;
  color: #ff6a00;
}

.yt-panel__empty {
  margin: 0;
  font-size: 13px;
  color: #888;
}

@media (max-width: 1100px) {
  .yt-panel {
    max-height: min(70vh, 520px);
    overflow: auto;
  }
}

@media (max-width: 900px) {
  .yt-search {
    min-height: 40px;
    padding: 3px 3px 3px 10px;
    gap: 0;
  }

  .yt-search__divider {
    display: none;
  }

  /* Compact location: icon (+ badge) only */
  :deep(.yt-search__loc) {
    display: block;
    flex: 0 0 auto;
    max-width: none;
    min-width: 0;
    margin-right: 2px;
  }

  :deep(.yt-search__location) {
    width: 36px;
    min-height: 36px;
    padding: 0;
    justify-content: center;
    gap: 0;
    border-radius: 50%;
  }

  :deep(.yt-search__chips),
  :deep(.yt-search__loc-placeholder) {
    display: none;
  }

  :deep(.yt-search__location > .fa-location-dot) {
    font-size: 15px;
  }

  :deep(.yt-search__loc.is-has-value .yt-search__location) {
    background: #e8e8e8;
  }

  .yt-search__submit {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 50%;
  }

  .yt-search__submit-label {
    display: none;
  }

  .yt-search__submit-icon {
    display: inline-block;
    font-size: 14px;
  }

  .yt-panel {
    left: -8px;
    right: -8px;
    padding: 14px;
    border-radius: 12px;
  }

  .yt-panel__top {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .yt-search__input {
    font-size: 14px;
  }

  .yt-search__submit {
    width: 32px;
    height: 32px;
  }

  .yt-panel {
    top: calc(100% + 6px);
    max-height: min(65vh, 420px);
  }

  .yt-panel__recent-count {
    display: none;
  }
}

@media (max-width: 480px) {
  .yt-search {
    padding: 3px 3px 3px 8px;
  }

  .yt-search__field > i {
    display: none;
  }

  .yt-search__submit {
    width: 32px;
    height: 32px;
    padding: 0;
    font-size: 13px;
  }

  :deep(.yt-search__location) {
    width: 32px;
    min-height: 32px;
  }

  .yt-panel {
    left: -4px;
    right: -4px;
    padding: 12px;
  }
}
</style>
